import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Client } from 'entities';

import Billing from './Billing';

export type ClientModal = 'BILLING';

interface ClientContext {
  selectedClient: Client | null;
}

interface ClientOptionsContext {
  openClientModal(type: ClientModal, client: Client): void;
  closeClientModal(): void;
}

const context = createContext({} as ClientContext);

const actionsContext = createContext({} as ClientOptionsContext);

function ClientOptionsProvider({ children }: { children: ReactNode }): JSX.Element {
  const [openModal, setOpenModal] = useState<ClientModal | null>(null);
  const [selectedClient, setClient] = useState<Client | null>(null);

  const openClientModal = useCallback((type: ClientModal, client: Client) => {
    setOpenModal(type);
    setClient(client);
  }, []);

  const closeClientModal = useCallback(() => {
    setOpenModal(null);
    setClient(null);
  }, []);

  const clientValue = useMemo(
    () => ({
      selectedClient,
    }),
    [selectedClient],
  );

  const actionValue = useMemo(
    () => ({
      closeClientModal,
      openClientModal,
    }),
    [closeClientModal, openClientModal],
  );

  return (
    <context.Provider value={clientValue}>
      <actionsContext.Provider value={actionValue}>
        <Billing open={openModal === 'BILLING'} close={closeClientModal} />

        {children}
      </actionsContext.Provider>
    </context.Provider>
  );
}

function useClient(): ClientContext {
  const ctx = useContext(context);

  if (!('selectedClient' in ctx)) {
    throw new Error('useClient must be used within the ClientProvider');
  }

  return ctx;
}

function useClientOptions(): ClientOptionsContext {
  const ctx = useContext(actionsContext);

  if (!ctx.openClientModal) {
    throw new Error('useClientOptions must be used within the ClientOptionsProvider');
  }

  return ctx;
}

export { ClientOptionsProvider, useClientOptions, useClient };
